import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  BarChart3, Briefcase, Clock, DollarSign, TrendingUp,
  User, Building2, CheckCircle2, Download, Sparkles, Loader2,
  Lightbulb,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { onsiteApiFetch } from '../config/api.js';
import { formatMinutes } from '../lib/utils.js';
import type { ElementType } from 'react';

interface ReportInsight {
  text: string;
  impact?: 'low' | 'medium' | 'high';
  action?: string;
  icon?: string;
}

interface ReportSummary {
  total_jobs: number;
  completed_jobs: number;
  completion_rate: number;
  total_billable_minutes: number;
  total_parts_revenue: number;
  by_technician: Array<{ name: string; total: number; completed: number; total_minutes: number }>;
  by_client: Array<{ name: string; total: number; completed: number }>;
}

export default function ReportsPage() {
  const { orgId } = useAuth();
  const [range, setRange] = useState('30');
  const [insights, setInsights] = useState<ReportInsight[] | null>(null);
  const [insightsError, setInsightsError] = useState<string | null>(null);

  const from = new Date(Date.now() - parseInt(range, 10) * 86400000).toISOString().substring(0, 10);

  const { data, isLoading } = useQuery({
    queryKey: ['reports', 'summary', orgId, range],
    queryFn: () => onsiteApiFetch<ReportSummary>(
      `/api/v1/onsite/jobs/reports/summary?from=${from}`
    ),
    enabled: !!orgId,
  });

  const fetchInsights = useMutation({
    mutationFn: () =>
      onsiteApiFetch<{ insights: ReportInsight[] }>(
        '/api/v1/onsite/reports/insights',
        { method: 'POST', body: JSON.stringify({ from }) }
      ),
    onSuccess: d => { setInsights(d.insights ?? []); setInsightsError(null); },
    onError: e => setInsightsError((e as Error).message),
  });

  async function exportCsv() {
    const base = import.meta.env.VITE_ONSITE_API_URL ?? 'https://api.theonestack.com';
    try {
      const res = await fetch(
        `${base}/api/v1/onsite/reports/summary?format=csv&from=${from}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(body.error ?? `Export failed: ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `onsite-report-${from}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert((e as Error).message);
    }
  }

  const report = data;

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-5xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Reports</h1>
            <p className="text-sm text-muted-foreground">Job performance and field operations analytics</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={range}
              onChange={e => setRange(e.target.value)}
              className="rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={() => fetchInsights.mutate()}
              disabled={fetchInsights.isPending}
              className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {fetchInsights.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              AI Insights
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : !report ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-muted-foreground">
            <BarChart3 className="h-12 w-12" />
            <p className="text-lg font-medium">No data available</p>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-5">
              <KpiCard label="Total Jobs" value={report.total_jobs.toString()} icon={Briefcase} color="text-blue-400" />
              <KpiCard label="Completed" value={report.completed_jobs.toString()} icon={CheckCircle2} color="text-green-400" />
              <KpiCard label="Completion Rate" value={`${report.completion_rate}%`} icon={TrendingUp} color="text-primary" />
              <KpiCard label="Billable Time" value={formatMinutes(report.total_billable_minutes)} icon={Clock} color="text-cyan-400" />
              <KpiCard label="Parts Revenue" value={`$${report.total_parts_revenue.toFixed(0)}`} icon={DollarSign} color="text-emerald-400" />
            </div>

            {/* AI Insights panel */}
            {(insights || insightsError) && (
              <div className="mb-8 rounded-xl border border-primary/30 bg-primary/5 p-4">
                <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Sparkles className="h-4 w-4 text-primary" />
                  AI Insights
                </h2>
                {insightsError && (
                  <p className="text-sm text-red-400">AI assistant unavailable: {insightsError}</p>
                )}
                {insights && insights.length === 0 && (
                  <p className="text-sm text-muted-foreground">No insights for this range.</p>
                )}
                {insights && insights.length > 0 && (
                  <ul className="space-y-2">
                    {insights.map((ins, i) => (
                      <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3">
                        <Lightbulb className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground">{ins.text}</p>
                          {ins.action && <p className="mt-1 text-xs text-muted-foreground">{ins.action}</p>}
                        </div>
                        {ins.impact && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${
                            ins.impact === 'high' ? 'bg-red-500/20 text-red-400'
                            : ins.impact === 'medium' ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {ins.impact}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* By Technician */}
            <div className="mb-8">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">By Technician</h2>
              {report.by_technician.length === 0 ? (
                <div className="rounded-xl border border-border bg-card py-6 text-center text-sm text-muted-foreground">
                  No technician data
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-card">
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Technician</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Jobs</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Completed</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Rate</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_technician.map((tech, i) => (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/50">
                          <td className="px-4 py-3 text-foreground">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {tech.name || 'Unassigned'}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-foreground">{tech.total}</td>
                          <td className="px-4 py-3 text-right text-foreground">{tech.completed}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={tech.total > 0 && (tech.completed / tech.total) >= 0.8 ? 'text-green-400' : 'text-muted-foreground'}>
                              {tech.total > 0 ? Math.round((tech.completed / tech.total) * 100) : 0}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-foreground">{formatMinutes(tech.total_minutes)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* By Client */}
            <div>
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">By Client</h2>
              {report.by_client.length === 0 ? (
                <div className="rounded-xl border border-border bg-card py-6 text-center text-sm text-muted-foreground">
                  No client data
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border border-border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-card">
                        <th className="px-4 py-3 text-left font-medium text-muted-foreground">Client</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Jobs</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Completed</th>
                        <th className="px-4 py-3 text-right font-medium text-muted-foreground">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.by_client
                        .sort((a, b) => b.total - a.total)
                        .slice(0, 20)
                        .map((client, i) => (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/50">
                          <td className="px-4 py-3 text-foreground">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              {client.name}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-foreground">{client.total}</td>
                          <td className="px-4 py-3 text-right text-foreground">{client.completed}</td>
                          <td className="px-4 py-3 text-right text-muted-foreground">
                            {client.total > 0 ? Math.round((client.completed / client.total) * 100) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, color }: {
  label: string; value: string; icon: ElementType; color: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className={`mb-2 ${color}`}>
        <Icon className="h-5 w-5" />
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
